/**
 * Las imágenes de la marca para las plantillas (fase 2): la lista, subir y borrar. Lo usan la página «Imágenes» y el
 * selector del editor de plantillas, así las dos dicen lo mismo y suben igual.
 *
 * Subir lee el archivo en el navegador (base64), lo manda y suma la imagen arriba de la lista. Si ya estaba (la misma
 * imagen subida antes), devuelve esa: el backend no guarda copias.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { oficialApi } from "../api";
import { leerComoBase64, problemaDeArchivo } from "../logic/imagenes";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import type { ImagenOficial, ImagenSubida, ListaImagenes } from "../types";

export interface ImagenesOficial {
  lista: ListaImagenes | null;
  cargando: boolean;
  /** Lo que falló al traer la lista. */
  error: string;
  subiendo: boolean;
  /** Sube el archivo y devuelve la imagen (o `null` con el motivo en `errorSubida`). */
  subir: (f: File) => Promise<ImagenSubida | null>;
  errorSubida: string;
  /** Borra la imagen; devuelve el texto del problema o `null` si salió. */
  borrar: (id: string) => Promise<string | null>;
  recargar: () => Promise<void>;
}

export function useImagenesOficial(): ImagenesOficial {
  const [lista, setLista] = useState<ListaImagenes | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [errorSubida, setErrorSubida] = useState("");
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const recargar = useCallback(async () => {
    setCargando(true);
    let l: { lista?: ListaImagenes; error: string };
    try {
      const r = leerRespuesta<ListaImagenes>(await oficialApi.imagenes());
      l = r.ok ? { lista: r.data, error: "" } : { error: textoError(r) };
    } catch (e) {
      l = { error: textoError(leerError(e)) };
    }
    if (!montado.current) return;
    if (l.lista) setLista(l.lista);
    setError(l.error);
    setCargando(false);
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const subir = useCallback(
    async (f: File): Promise<ImagenSubida | null> => {
      const problema = problemaDeArchivo(f, lista?.maxBytes);
      if (problema) {
        setErrorSubida(problema);
        return null;
      }
      setSubiendo(true);
      setErrorSubida("");
      let salida: ImagenSubida | null = null;
      let falla = "";
      try {
        const r = leerRespuesta<ImagenSubida>(await oficialApi.subirImagen({ nombreArchivo: f.name, contenido: await leerComoBase64(f) }));
        if (r.ok) salida = r.data;
        else falla = textoError(r);
      } catch (e) {
        const x = leerError(e);
        falla = x.status === 413 && !x.error ? "La imagen pesa más de 5 MB, el máximo de WhatsApp." : textoError(x);
      }
      if (!montado.current) return salida;
      setSubiendo(false);
      if (!salida) {
        setErrorSubida(falla);
        return null;
      }
      const nueva: ImagenOficial = salida.imagen;
      setLista((l) => (l ? { ...l, imagenes: [nueva, ...l.imagenes.filter((i) => i.id !== nueva.id)] } : l));
      return salida;
    },
    [lista?.maxBytes],
  );

  const borrar = useCallback(async (id: string): Promise<string | null> => {
    try {
      const r = leerRespuesta<{ borrada: true }>(await oficialApi.borrarImagen(id));
      if (!r.ok) return textoError(r);
    } catch (e) {
      return textoError(leerError(e));
    }
    if (montado.current) setLista((l) => (l ? { ...l, imagenes: l.imagenes.filter((i) => i.id !== id) } : l));
    return null;
  }, []);

  return { lista, cargando, error, subiendo, subir, errorSubida, borrar, recargar };
}
